import { BODY_TYPE, COMPASS, FRAMES, HUD, SPACING, TOOL_BELT_SLOTS, UI_LOGICAL_SIZE, pixelType, uiScaleOf, type UiView } from "odyssey-shared";
import { KIT_METRICS, centreOffset } from "../../ui/kit/metrics.ts";
import { pixelTextWidth } from "../../ui/kit/pixel-text.ts";
import { SMALL_DIGITS } from "../../ui/kit/small-digits.ts";
import { say } from "../../words.ts";
import { ABILITY_SLOT, BAR_SLOT_COUNT } from "../bar/layout.ts";

/**
 * The HUD's slots (P1.9-T13; ADR-061, amendment "the design system lands", "full screen, with the HUD on edge
 * anchors"): every HUD element through Round 3 has a slot here, empty until the row that builds it, so the
 * layout leaves room for all of them. The game fills the window, so nothing is laid out on a stage: each slot
 * hangs from an edge of the logical area, or from its centre line, by logical offsets taken from the export's
 * `HUD_SLOTS` (its 4 px safe margin and 2 px gutter are the token table's `HUD`), and the area is the window's
 * device size over the UI scale, rounded down (`hudLayout`), so a slot sits on whole logical pixels however
 * large the window is. On the export's 640 by 360 grid every slot sits where the export puts it.
 *
 * A Now slot's size derives from the tokens (the frames' insets, the kit's metrics, the pixel face's measured
 * line and advances), so a measured font moves it, and a slot stacked under one moves with it; its anchored
 * edges stay the export's. A reserved slot keeps the extent the export sets aside for it until the row that
 * builds its surface derives that surface's size the same way. Where the export has no slot (the Region
 * banner, raid frames, the dungeon HUD and the guild war score) the surface board and ADR-061 place one.
 *
 * Two slots may overlap only when one names the other in `shares`: a pair that never shows at once, or a
 * clash the row building the later surface resolves. The centre, about 200 by 120 around the character, is
 * never reserved.
 *
 * The table is the export's grid. Where a slot stands on an area smaller than the grid, the room the chat panel has
 * there and the pairs an area too small may squeeze together are `area-slots.ts`'s (ADR-061's amendment of
 * 2026-10-06); a surface reads its place through `slotRect` there.
 */

/**
 * The edge, corner or centre line a slot hangs from: a vertical edge (top or bottom) and a horizontal one
 * (left or right) or the vertical centre line.
 */
export type HudAnchor = "top-left" | "top-centre" | "top-right" | "bottom-left" | "bottom-centre" | "bottom-right";

/**
 * The brief's tier of the surface a slot holds (brief section 3): Now surfaces are drawn today; Next and Later
 * slots are reservations.
 */
export type HudTier = "now" | "next" | "later";

/**
 * Every slot's name: the export's eighteen, the four ADR-061 adds, the maintenance strip's (ADR-078), the stance
 * button's (P2.2-T13), the tool belt's (P2.26-T17), the connection mark's (P1.9-T32) and the performance panel's (ADR-087).
 */
export type HudSlotId =
  | "vitals"
  | "status"
  | "party"
  | "raid"
  | "target"
  | "targetStatus"
  | "duel"
  | "guildWar"
  | "boss"
  | "dungeon"
  | "banner"
  | "clock"
  | "minimap"
  | "location"
  | "quest"
  | "toasts"
  | "chat"
  | "cast"
  | "message"
  | "xp"
  | "abilities"
  | "stance"
  | "tools"
  | "connection"
  | "menu"
  | "maintenance"
  | "perf";

/**
 * One slot. `x` is measured from the anchored side: from the left edge to the slot's left, from the right edge
 * to the slot's right, or, on the centre line, a shift of the slot from its centred place (whose left is half
 * the area less half the slot, each rounded down). `y` is measured from the top edge to the slot's top or from
 * the bottom edge to the slot's bottom. Sizes and offsets are logical pixels.
 */
export interface HudSlot {
  readonly anchor: HudAnchor;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly tier: HudTier;
  readonly shares?: readonly HudSlotId[];
}

/**
 * The logical area the HUD is laid out on, in logical pixels, and whether the viewer chose Ability bar: Five at a time
 * (`barFive`, P1.9-T133), which pages the bar on an area of any width (`barPaged`); absent is All 10.
 */
export interface HudArea {
  readonly width: number;
  readonly height: number;
  readonly barFive?: boolean;
  readonly touch?: boolean;
  readonly safe?: SafeInsets;
}

/**
 * The room a notch, a rounded corner or a system bar takes from each edge of the page, in logical pixels, which a touch
 * layout keeps its controls out of (`env(safe-area-inset-*)`, read by `touch/safe-area.ts`): the page covers the whole
 * screen (`viewport-fit=cover`, ADR-061's amendment of 2026-10-31, decision 4). Absent where a display has none.
 */
export interface SafeInsets {
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly left: number;
}

/**
 * The HUD's area and the UI scale it was computed at.
 */
export interface HudLayout extends HudArea {
  readonly scale: number;
}

/**
 * A slot's rectangle from the area's top left, in logical pixels.
 */
export interface SlotRect {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/**
 * The most lines the message line grows to before a longer reply is cut short with U+2026: an engineering
 * limit of its slot, not design copy (ADR-061, amendment "the export is a visual reference").
 */
export const MESSAGE_MAX_LINES = 4;

/**
 * The key the menu slot's key glyph names (P1.9-T14: "the menu slot carries the Esc key glyph and a menu
 * button").
 */
export const MENU_KEY = "Esc";

/**
 * The centre kept clear around the character, in logical pixels: the export's "about 200 by 120".
 */
export const HUD_CENTRE = { width: 200, height: 120 } as const;

/**
 * The export's width for the vitals slot (`ui_kits/game_client/Hud.jsx`).
 */
const VITALS_EXPORT_WIDTH = 132;

/**
 * The player card's width, 7 wider than the export's slot (ADR-061, amendment "the player card keeps one width", the
 * author's ruling "Widen the card a little"): the least whole width at which the header leaves the name 72, twelve
 * glyphs of the pixel face's usual 6, beside the flag mark, the in-combat mark's reserved slot and "Lv 99"
 * (`nameRoom` in `vitals.ts`, held by its test). The card's content is this less the HUD frame's insets and padding.
 */
const VITALS_SLOT_WIDTH = VITALS_EXPORT_WIDTH + 7;

/**
 * How the player card lays out what it shows (ADR-061, amendment "the player card"): the header row of a
 * `flagSize` mark, `nameGap` before the name and `levelGap` between the name and the level at the right; then,
 * `rowGap` apart, the health bar and the Stamina and Focus bars. Every bar is an `edge` around its fill, which on
 * the thin bars is `thinFill` rows. The health value is the kit's small digits inside their outline.
 */
export const VITALS = { flagSize: 8, nameGap: SPACING[2], levelGap: SPACING[2], rowGap: SPACING[0], edge: 1, thinFill: SPACING[1] } as const;

/**
 * The outer size of a HUD frame around content: its insets and the kit's HUD padding on every side.
 *
 * @param width The content's width in logical pixels.
 * @param height The content's height in logical pixels.
 * @returns The frame's width and height.
 */
function hudFramed(width: number, height: number): { readonly width: number; readonly height: number } {
  const insets = FRAMES.hud.insets;
  const padding = KIT_METRICS.hud.padding;
  return { width: insets.left + padding + width + padding + insets.right, height: insets.top + padding + height + padding + insets.bottom };
}

/**
 * The player card's content (`vitalsCard`).
 */
export const VITALS_CARD = vitalsCard();

/**
 * Lays out the player card's content: as wide as the export's slot leaves inside the HUD frame; the header as tall
 * as the 8 px flag row; the health bar the small digits and their outline above and below between its edges, so
 * the outlined value lies wholly inside the bar, its outline on the fill's highlight and shade rows; the thin bars
 * `thinFill` between their edges. `inner` is what a bar's fill covers at the content's width, and `valueTop` where
 * the value's outlined box starts, from the top of the health bar's inside.
 *
 * @returns The content's width and height, the header's and the bars' heights, a bar's inside and the value's top,
 *   in logical pixels.
 */
function vitalsCard(): { readonly width: number; readonly height: number; readonly header: number; readonly health: number; readonly thin: number; readonly inner: number; readonly valueTop: number } {
  const width = VITALS_SLOT_WIDTH - hudFramed(0, 0).width;
  const header = KIT_METRICS.flag[VITALS.flagSize].height;
  const health = VITALS.edge + SMALL_DIGITS.outline + SMALL_DIGITS.height + SMALL_DIGITS.outline + VITALS.edge;
  const thin = VITALS.edge + VITALS.thinFill + VITALS.edge;
  const height = header + VITALS.rowGap + health + VITALS.rowGap + thin + VITALS.rowGap + thin;
  return { width, height, header, health, thin, inner: width - 2 * VITALS.edge, valueTop: 0 };
}

/**
 * The export's width for the target frame's slot, which the frame keeps (P2.2-T3).
 */
const TARGET_SLOT_WIDTH = 116;

/**
 * The target frame's content (P2.2-T3, the design's `TargetBox`): as wide as the export's slot leaves inside the HUD
 * frame; a header row as the player card's (the flag mark, the name, the level at the right), then, the card's row gap
 * below, a health bar as tall as the player card's, so the two cards' bars and digits match.
 */
export const TARGET_CARD = { width: TARGET_SLOT_WIDTH - hudFramed(0, 0).width, header: VITALS_CARD.header, health: VITALS_CARD.health, height: VITALS_CARD.header + VITALS.rowGap + VITALS_CARD.health } as const;

/**
 * A HUD frame's outer size around a card's content width, for a card placed beside another.
 *
 * @param width The content's width in logical pixels.
 * @returns The frame's outer width.
 */
export function framedWidth(width: number): number {
  return hudFramed(width, 0).width;
}

/**
 * The player card's size at the slot: its content inside the HUD frame. The card keeps this width whatever it
 * shows: the header cuts a name that does not fit (`fitName` in `vitals.ts`).
 *
 * @returns The width and height.
 */
function vitalsSize(): { readonly width: number; readonly height: number } {
  return hudFramed(VITALS_CARD.width, VITALS_CARD.height);
}

/**
 * Where the menu slot's parts sit inside it, from its top left in logical pixels (P1.9-T14): the Esc key
 * glyph at the left (the key frame, painted on the button's insets, and the key's padding around the key's
 * name) and the menu icon button a gutter after it, each set down the middle of the slot, which is as tall
 * as the taller, rounded down to a whole pixel.
 *
 * @returns The key glyph's and the button's rectangles.
 */
function menuLayout(): { readonly key: SlotRect; readonly button: SlotRect } {
  const insets = FRAMES.button.insets;
  const keyWidth = insets.left + KIT_METRICS.key.paddingX + pixelTextWidth(MENU_KEY) + KIT_METRICS.key.paddingX + insets.right;
  const height = Math.max(KIT_METRICS.key.height, KIT_METRICS.iconButton.height);
  return {
    key: { left: 0, top: centreOffset(height, KIT_METRICS.key.height), width: keyWidth, height: KIT_METRICS.key.height },
    button: { left: keyWidth + HUD.gutter, top: centreOffset(height, KIT_METRICS.iconButton.height), width: KIT_METRICS.iconButton.width, height: KIT_METRICS.iconButton.height },
  };
}

/**
 * The menu slot's parts in place: the Esc key glyph and the menu button (`menuLayout`).
 */
export const MENU_LAYOUT = menuLayout();

/**
 * The menu slot's size: its two parts a gutter apart, as tall as the taller.
 *
 * @returns The width and height.
 */
function menuSize(): { readonly width: number; readonly height: number } {
  const { key, button } = MENU_LAYOUT;
  return { width: button.left + button.width, height: Math.max(key.top + key.height, button.top + button.height) };
}

/**
 * Where a slot stacked under another starts: the other's bottom and a gutter.
 *
 * @param above The slot above, anchored to the top.
 * @returns The offset from the top edge.
 */
function below(above: HudSlot): number {
  return above.y + above.height + HUD.gutter;
}

/**
 * The bottom of the top-centre column and band, on the export's grid: a gutter above the clear centre.
 */
const COLUMN_BOTTOM = UI_LOGICAL_SIZE.height / 2 - HUD_CENTRE.height / 2 - HUD.gutter;

const VITALS_SIZE = vitalsSize();
const MENU_SIZE = menuSize();

/**
 * How far the clock's right edge stands from the area's right edge: the export's 84 and the 14 the minimap's slot grew
 * by, so the clock clears the slot and keeps its gutter beside it.
 */
const CLOCK_FROM_RIGHT = 98;

/**
 * The toast stack's top: the export's 204 and the 14 the column above it moved down.
 */
const TOASTS_TOP = 218;

/**
 * The connection mark (P1.9-T32, the design's `ConnectionMark`): a chip in the HUD frame holding an 8 px glyph, the word
 * and its figure ("Reconnecting" and the attempt), as wide as the longest of those words and as tall as the pixel
 * face's line, set down where the player's status effects stand, which it stands in for while it shows (the effects of
 * a world held for a reconnect are stale). Its measure is the words' in the pixel face, so a reworded draft moves it.
 *
 * @returns The mark's outer size in logical pixels.
 */
function connectionSize(): { readonly width: number; readonly height: number } {
  const gap = SPACING[3];
  const words = Math.max(
    pixelTextWidth(say("hud.connection.reconnecting")) + gap + pixelTextWidth(say("hud.connection.attempt", { attempt: "5", total: "5" })),
    pixelTextWidth(say("hud.connection.slow")),
    pixelTextWidth(say("hud.connection.restored")),
    pixelTextWidth(say("hud.connection.offline"))
  );
  return hudFramed(8 + gap + words, pixelType("label").lineHeight);
}

/**
 * The connection mark's size, measured once from the words as they stand.
 */
const CONNECTION_SIZE = connectionSize();

/**
 * The top-left stack: the vitals frame, the player's status effects and the party frames under it.
 */
const vitals: HudSlot = { anchor: "top-left", x: HUD.margin, y: HUD.margin, ...VITALS_SIZE, tier: "now" };
const status: HudSlot = { anchor: "top-left", x: HUD.margin, y: below(vitals), width: 132, height: 30, tier: "now" };
const connection: HudSlot = { anchor: "top-left", x: HUD.margin, y: below(vitals), ...CONNECTION_SIZE, tier: "now", shares: ["status"] };
const party: HudSlot = { anchor: "top-left", x: HUD.margin, y: below(status), width: 88, height: 122, tier: "next" };

/**
 * The target frame and its effects, beside the vitals frame: the slot at the export's place, its size its card inside
 * the HUD frame (P2.2-T3). The player card ends at 143, one short of the slot, so the frame and its effects row are
 * drawn a gutter right of the card, at 145 (`targetRowLeft` and the frame's own left).
 */
const target: HudSlot = { anchor: "top-left", x: 144, y: HUD.margin, ...hudFramed(TARGET_CARD.width, TARGET_CARD.height), tier: "now" };
const targetStatus: HudSlot = { anchor: "top-left", x: 144, y: below(target), width: 116, height: 30, tier: "now" };

/**
 * The top-centre column: the duel HUD, the boss frame under it, and the dungeon HUD filling the rest of the
 * column above the clear centre.
 */
const duel: HudSlot = { anchor: "top-centre", x: 0, y: HUD.margin, width: 108, height: 30, tier: "next" };
const boss: HudSlot = { anchor: "top-centre", x: 0, y: below(duel), width: 108, height: 18, tier: "later" };
const dungeon: HudSlot = { anchor: "top-centre", x: 0, y: below(boss), width: 108, height: COLUMN_BOTTOM - below(boss), tier: "later" };

/**
 * The top-right stack: the minimap, the location readout and the quest tracker, with the clock beside the
 * minimap and the toast stack under the tracker. The minimap's slot is the compass's 90 square (ADR-061's amendment
 * of 2026-10-14, "a square compass round the minimap", decisions 1 and 2: the frame's 3, the band's 7, the field's 70,
 * the band's 7 and the frame's 3), 14 more than the export's 76, so the location readout and the quest tracker, which
 * hang from its bottom, move down 14, the clock stands 14 further from the right edge so it clears the slot, and the
 * toast stack starts 14 lower.
 */
const minimap: HudSlot = { anchor: "top-right", x: HUD.margin, y: HUD.margin, width: COMPASS.slot, height: COMPASS.slot, tier: "next" };
const location: HudSlot = { anchor: "top-right", x: HUD.margin, y: below(minimap), width: 116, height: 36, tier: "next" };
const quest: HudSlot = { anchor: "top-right", x: HUD.margin, y: below(location), width: 116, height: 80, tier: "next" };

/**
 * The bottom row: the ability bar on the bottom margin with the XP bar over it, the cast bar above them. The bar holds
 * ten slots (the author's ruling of 2026-10-02, P2.2-T11), each the design's 22 square slot a pixel apart, 229 wide as
 * the export's and the XP bar over it are.
 */
const abilities: HudSlot = { anchor: "bottom-centre", x: 0, y: HUD.margin, width: BAR_SLOT_COUNT * ABILITY_SLOT.size + (BAR_SLOT_COUNT - 1) * ABILITY_SLOT.gap, height: ABILITY_SLOT.size, tier: "now" };

/**
 * The stance button (P2.2-T13, ADR-073's amendment "the stance: a weapon put away"): one slot's 22 square on the bar's
 * bottom margin, a gutter right of the bar, since the bar's left side meets the chat panel's slot at the export's
 * grid. The export has no such slot; its place is the amendment's.
 */
const stance: HudSlot = { anchor: "bottom-centre", x: Math.ceil(abilities.width / 2) + HUD.gutter + Math.floor(ABILITY_SLOT.size / 2), y: HUD.margin, width: ABILITY_SLOT.size, height: ABILITY_SLOT.size, tier: "now" };

/**
 * The tool belt's geometry (ADR-142 decision 3b and its note of 2026-10-31, the mockup `tool-belt-mockup`'s chip): one chip
 * `TOOL_BELT.chip` square (a bar slot's size) showing the tool in hand, `TOOL_BELT.gap` right of the stance button on the
 * bar's baseline, fanning the four slots `TOOL_BELT.fan` square, `TOOL_BELT.fanGap` apart, in a frame `TOOL_BELT.fanPad`
 * inside its `TOOL_BELT.fanEdge` border, centred over it.
 */
export const TOOL_BELT = { slots: TOOL_BELT_SLOTS, gap: 4, chip: ABILITY_SLOT.size, fan: ABILITY_SLOT.size, touchFan: 32, fanGap: ABILITY_SLOT.gap, fanPad: 2, fanEdge: 1 } as const;

/**
 * The tool chip's slot on the grid: the stance button's right edge and the gap on, on the bar's bottom margin. Where the
 * bar pages the slot stands after the stance button as it stands there (`slotOn`).
 */
const tools: HudSlot = { anchor: "bottom-centre", x: stance.x - Math.floor(ABILITY_SLOT.size / 2) + ABILITY_SLOT.size + TOOL_BELT.gap + Math.floor(TOOL_BELT.chip / 2), y: HUD.margin, width: TOOL_BELT.chip, height: TOOL_BELT.chip, tier: "now" };

/**
 * The performance panel (ADR-087 decision 8): its content's width, room for a row's label and its widest value, and
 * its lines of caption text: the title, nine client rows, the server heading, five server rows, the table's head and
 * its four rows.
 */
export const PERF_PANEL = { width: 132, lines: 21 } as const;

/**
 * The performance panel's slot: the top-right corner, over the column the minimap, the location readout and the quest
 * tracker reserve (all Next) and the clock's place (Later), above the toasts; it covers no surface built today, and the
 * rows that build those decide which gives way while the panel shows. Its height is its lines and the divider between
 * the halves inside the HUD frame.
 */
const perf: HudSlot = { anchor: "top-right", x: HUD.margin, y: HUD.margin, ...hudFramed(PERF_PANEL.width, PERF_PANEL.lines * BODY_TYPE.caption.lineHeight + KIT_METRICS.divider.thickness + 2 * KIT_METRICS.divider.margin), tier: "now", shares: ["minimap", "location", "quest", "clock"] };

/**
 * Every slot. The offsets not built from the margin, the gutter or the slot above are the export's. The
 * message line is as wide as its text and centred, so only a long line, as wide as its slot, reaches over the
 * chat panel on the 640 grid (as the export's one-line slot already does), drawing over the panel while it
 * shows (ADR-068 decision 8), and one of three or four lines covers the cast bar and a corner of the toast stack
 * while it shows; the rows building those decide which gives way. The chat slot is the panel at its own size; a
 * resized panel grows up and right from the slot's bottom-left corner within `CHAT_RANGE` and `chatRoom`, where it
 * reaches the party and raid frames' reservations, which the slot therefore shares (ADR-061's amendment "the chat
 * panel resizes"): the rows that build those frames decide which gives way.
 */
export const HUD_SLOTS: Readonly<Record<HudSlotId, HudSlot>> = {
  vitals,
  status,
  party,
  raid: { anchor: party.anchor, x: party.x, y: party.y, width: 120, height: 122, tier: "later", shares: ["party"] },
  target,
  targetStatus,
  duel,
  guildWar: { anchor: duel.anchor, x: duel.x, y: duel.y, width: duel.width, height: duel.height, tier: "later", shares: ["duel"] },
  boss,
  dungeon,
  banner: { anchor: "top-centre", x: 0, y: 40, width: 300, height: COLUMN_BOTTOM - 40, tier: "next", shares: ["targetStatus", "boss", "dungeon"] },
  clock: { anchor: "top-right", x: CLOCK_FROM_RIGHT, y: HUD.margin, width: 80, height: 14, tier: "later" },
  minimap,
  location,
  quest,
  toasts: { anchor: "top-right", x: HUD.margin, y: TOASTS_TOP, width: 128, height: 84, tier: "now" },
  chat: { anchor: "bottom-left", x: HUD.margin, y: HUD.margin, width: 198, height: 122, tier: "now", shares: ["party", "raid"] },
  cast: { anchor: "bottom-centre", x: 0, y: 66, width: 120, height: 12, tier: "now" },
  message: { anchor: "bottom-centre", x: 0, y: 44, width: 384, height: KIT_METRICS.message.height + (MESSAGE_MAX_LINES - 1) * BODY_TYPE.body.lineHeight, tier: "now", shares: ["cast", "chat", "toasts"] },
  xp: { anchor: "bottom-centre", x: 0, y: 27, width: 229, height: 5, tier: "next" },
  abilities,
  stance,
  tools,
  connection,
  menu: { anchor: "bottom-right", x: HUD.margin, y: HUD.margin, ...MENU_SIZE, tier: "now" },
  maintenance: { anchor: "top-centre", x: 0, y: 40, width: 184, height: 78, tier: "now", shares: ["banner", "boss", "dungeon", "targetStatus"] },
  perf,
};

/**
 * The logical area the HUD is laid out on for a window: its device size over the UI scale, rounded down, so
 * the half logical pixel a window can leave over (1017 device pixels at scale 2) is unused and every slot
 * lies on whole logical pixels from the top left.
 *
 * @param device The window in device pixels, or its render size above render resolution Full (`UiView`).
 * @param safe The insets of the page's safe area in device pixels, from the page's own probe; none when absent.
 * @returns The area and the scale (`uiScaleOf`): 2 on a 683 by 325 area for a 1366 by 650 window.
 * @example
 * hudLayout({ width: 1920, height: 1017 });
 * // { width: 960, height: 508, scale: 2 }
 */
export function hudLayout(device: UiView, safe?: SafeInsets): HudLayout {
  const scale = uiScaleOf(device);
  const insets = safe === undefined ? undefined : { top: Math.ceil(safe.top / scale), right: Math.ceil(safe.right / scale), bottom: Math.ceil(safe.bottom / scale), left: Math.ceil(safe.left / scale) };
  return { width: Math.floor(device.width / scale), height: Math.floor(device.height / scale), scale, ...(device.touch === true ? { touch: true } : {}), ...(insets === undefined || (insets.top | insets.right | insets.bottom | insets.left) === 0 ? {} : { safe: insets }) };
}

/**
 * Where a slot lies on an area.
 *
 * @param slot The slot.
 * @param area The area, whole logical pixels.
 * @returns Its rectangle from the area's top left, whole logical pixels.
 * @example
 * resolveSlot(HUD_SLOTS.menu, { width: 960, height: 508 });
 * // { left: 902, top: 475, width: 54, height: 29 }
 */
export function resolveSlot(slot: HudSlot, area: HudArea): SlotRect {
  const [vertical, horizontal] = slot.anchor.split("-") as ["top" | "bottom", "left" | "centre" | "right"];
  const left = horizontal === "left" ? slot.x : horizontal === "right" ? area.width - slot.x - slot.width : Math.floor(area.width / 2) - Math.floor(slot.width / 2) + slot.x;
  const top = vertical === "top" ? slot.y : area.height - slot.y - slot.height;
  return { left, top, width: slot.width, height: slot.height };
}

/**
 * Whether two layouts lay the HUD out the same: the same area, scale, bar paging, touch mode and safe insets.
 *
 * @param a One layout.
 * @param b Another.
 * @returns True when nothing a slot reads differs.
 */
export function sameLayout(a: HudLayout, b: HudLayout): boolean {
  const x = a.safe;
  const y = b.safe;
  return a.width === b.width && a.height === b.height && a.scale === b.scale && (a.barFive === true) === (b.barFive === true) && (a.touch === true) === (b.touch === true) && (x?.top ?? 0) === (y?.top ?? 0) && (x?.right ?? 0) === (y?.right ?? 0) && (x?.bottom ?? 0) === (y?.bottom ?? 0) && (x?.left ?? 0) === (y?.left ?? 0);
}
